import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { ADMIN_SESSION_PATH } from '@mcikm-vote/shared';
import type { AdminAnmeldungAnfrage, AdminSitzungAuskunft } from '@mcikm-vote/shared';

import { apiAnfrage, ApiFehler } from '../api/anfrage';
import { verwerfeCsrfWert } from '../api/csrfSpeicher';

/** Abfrageschlüssel der Sitzungsauskunft. */
export const SITZUNG_SCHLUESSEL = ['admin', 'sitzung'] as const;

/**
 * Sitzungsauskunft (§5.2).
 *
 * Der Endpunkt ist serverseitig mit `@OhneAktivitaet()` gekennzeichnet: diese Abfrage verlängert
 * die 30-Minuten-Frist **nicht**. Deshalb wird hier auch bewusst **kein** `refetchInterval`
 * gesetzt — eine regelmäßige Abfrage würde die Sitzung ohnehin nicht am Leben halten und nur Last
 * erzeugen.
 */
export function useAdminSitzung(): UseQueryResult<AdminSitzungAuskunft, ApiFehler> {
  return useQuery<AdminSitzungAuskunft, ApiFehler>({
    queryKey: SITZUNG_SCHLUESSEL,
    queryFn: () => apiAnfrage<AdminSitzungAuskunft>(ADMIN_SESSION_PATH),
    // Eine fehlende Sitzung ist ein erwarteter Zustand, kein Fehler, der Wiederholung verdient.
    retry: false,
    staleTime: 10_000,
  });
}

export function useAnmelden(): UseMutationResult<
  AdminSitzungAuskunft,
  ApiFehler,
  AdminAnmeldungAnfrage
> {
  const queryClient = useQueryClient();

  return useMutation<AdminSitzungAuskunft, ApiFehler, AdminAnmeldungAnfrage>({
    mutationFn: (anfrage) =>
      apiAnfrage<AdminSitzungAuskunft>(ADMIN_SESSION_PATH, {
        methode: 'POST',
        koerper: anfrage,
      }),
    onSuccess: (auskunft) => {
      queryClient.setQueryData(SITZUNG_SCHLUESSEL, auskunft);
    },
  });
}

export function useAbmelden(): UseMutationResult<void, ApiFehler, void> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiFehler, void>({
    mutationFn: () => apiAnfrage<void>(ADMIN_SESSION_PATH, { methode: 'DELETE' }),
    onSettled: () => {
      // Auch bei einem Fehlschlag wird der lokale Zustand verworfen: der Serverzustand ist
      // verbindlich (§40.3), und die nächste Abfrage stellt ihn fest.
      verwerfeCsrfWert('admin');
      queryClient.removeQueries({ queryKey: SITZUNG_SCHLUESSEL });
    },
  });
}
